import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";

const SiteReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const { taskId } = useParams();

  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [formData, setFormData] = useState({
    status: "not ready",
    progress: 0,
    note: "",
    inspection_status: "TBD",
    inspection_schedule_date: "",
    estimate_start: "",
    estimate_finish: "",
    user_id: localStorage.getItem("userId")
  });
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [userId, setUserId] = useState("");
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (!role) {
      navigate("/");
      return;
    }

    if (!projectId || projectId.trim() === "") {
      navigate("/site_select_project");
      return;
    }

    fetch(`/api/tasks?projectId=${projectId}`)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch tasks");
        return res.json();
      })
      .then(data => setTasks(data))
      .catch(err => {
        console.error(err);
        setError("Failed to load tasks. Please try again later.");
      });
  }, [navigate, projectId]);

  useEffect(() => {
    if (!selectedTaskId) return;

    const fetchTaskDetails = async () => {
      try {
        const res = await fetch(`/api/tasks/${selectedTaskId}`);
        if (!res.ok) throw new Error("Failed to fetch task details");
        const data = await res.json();
        
        setFormData({
          status: data.status || "not ready",
          progress: data.progress || 0,
          note: "",
          inspection_status: data.inspection_status || "TBD",
          inspection_schedule_date: data.inspection_schedule_date ? data.inspection_schedule_date.split("T")[0] : "",
          estimate_start: data.estimate_start ? data.estimate_start.split("T")[0] : "",
          estimate_finish: data.estimate_finish ? data.estimate_finish.split("T")[0] : "",
          user_id: localStorage.getItem("userId")
        });
      } catch (err) {
        console.error(err);
        setError("Failed to load task details. Please try again later.");
      }
    };

    fetchTaskDetails();
  }, [selectedTaskId]);

  

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.type.startsWith("image/") && file.size <= 20 * 1024 * 1024);
    setImages(validFiles);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);

    console.log('selectedTaskId:', selectedTaskId);

    try {
      const updateData = {
        status: formData.status,
        progress: parseInt(formData.progress),
        inspection_status: formData.inspection_status,
        projectId: projectId,
        user_id: localStorage.getItem("userId")
      };

      if (formData.inspection_schedule_date) {
        updateData.inspection_schedule_date = formData.inspection_schedule_date;
      }
      if (formData.estimate_start) {
        updateData.estimate_start = formData.estimate_start;
      }
      if (formData.estimate_finish) {
        updateData.estimate_finish = formData.estimate_finish;
      }

      const updateRes = await fetch(`/api/tasks/${selectedTaskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });

      if (!updateRes.ok) {
        const updateData = await updateRes.json();
        throw new Error(updateData.error || "Update failed");
      }

      if (formData.note.trim() !== "") {
        const noteRes = await fetch(`/api/tasks/${selectedTaskId}/note?user_id=${localStorage.getItem("userId")}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            text: formData.note
          }),
        });

        if (!noteRes.ok) {
          const noteData = await noteRes.json();
          throw new Error(noteData.error || "Failed to add note");
        }
      }

      // 2. 上传图片（如有选择）
      if (images.length && selectedTaskId) {
        const imageFormData = new FormData();
        imageFormData.append("taskId", selectedTaskId);
        imageFormData.append("projectId", projectId);
        imageFormData.append("user_id", localStorage.getItem("userId"));
        images.forEach((img) => imageFormData.append("images", img));
        
        const imageUploadRes = await fetch("/api/construction/task/upload-images", {
          method: "POST",
          body: imageFormData,
        });

        const imageData = await imageUploadRes.json().catch(() => ({}));
        if (!imageUploadRes.ok) {
          throw new Error(imageData.error || "Failed to upload images");
        }

        if (imageData.thumbnail_all_ok === false) {
          window.alert(
            "Images saved, but thumbnail generation failed for at least one file. Display may use on-demand thumbnails until fixed."
          );
        }

        setImages([]);
      }

      alert("Task updated successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Error updating task:", error);
      setError(error.message || "Failed to update task");
    }
  };

  useEffect(() => {
    // 获取当前登录用户信息
    const user = JSON.parse(localStorage.getItem("userInfo") || "null");
    if (user) {
      setUserId(user._id || user.id || "");
      setUserName(user.name || "");
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold mb-6">Site Report</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Select Task</label>
            <select
              value={selectedTaskId}
              onChange={e => setSelectedTaskId(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              required
            >
              <option value="">-- Select Task --</option>
              {tasks.map(task => (
                <option key={task._id} value={task._id}>{task.task_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="not ready">Not Ready</option>
              <option value="waiting book">Waiting Book</option>
              <option value="booked">Booked</option>
              <option value="working">Working</option>
              <option value="complete">Complete</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Progress %</label>
            <input
              type="number"
              name="progress"
              value={formData.progress}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
              min="0"
              max="100"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Note</label>
            <textarea
              name="note"
              value={formData.note}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Inspection Status</label>
            <select
              name="inspection_status"
              value={formData.inspection_status}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="TBD">TBD</option>
              <option value="Not Required">Not Required</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Passed">Passed</option>
            </select>
          </div>

          {formData.inspection_status === "Scheduled" && (
            <div>
              <label className="block text-sm font-semibold mb-1">Inspection Date</label>
              <input
                type="date"
                name="inspection_schedule_date"
                value={formData.inspection_schedule_date}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold mb-1">Estimate Start Date</label>
            <input
              type="date"
              name="estimate_start"
              value={formData.estimate_start}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Estimate Finish Date</label>
            <input
              type="date"
              name="estimate_finish"
              value={formData.estimate_finish}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          {/* Image upload control */}
          <label className="block text-sm font-semibold mb-1">Upload Images (multiple, ≤20MB each):</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />

          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Update Task
          </button>
        </form>

        
      </div>
    </div>
  );
};

export default SiteReport; 